import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { activityApi, usersApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { useAuth } from "@/context/AuthContext";
import { Send, Trash2, AtSign, MessageSquare } from "lucide-react";

const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const fmt = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };

const renderBody = (text) =>
  text.split(/(@[\wäöüÄÖÜß.\- ]+?(?=\s|$|[,.;:!?]))/g).map((part, i) =>
    part.startsWith("@")
      ? <span key={i} className="text-primary font-medium">{part}</span>
      : <span key={i}>{part}</span>
  );

export const CustomerCommentsTab = ({ entityType = "customer", entityId }) => {
  const toast = useToast();
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [mentionIds, setMentionIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [query, setQuery] = useState("");
  const taRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [cRes, uRes] = await Promise.all([
        activityApi.getComments(entityType, entityId),
        usersApi.getAll(),
      ]);
      setComments(Array.isArray(cRes.data) ? cRes.data : []);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
    } catch (e) { console.debug("Kommentare laden:", e); }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const caret = e.target.selectionStart;
    const upto = v.slice(0, caret);
    const m = upto.match(/@([\wäöüÄÖÜß.\-]*)$/);
    if (m) { setQuery(m[1].toLowerCase()); setShowMenu(true); }
    else setShowMenu(false);
  };

  const pickUser = (u) => {
    const v = text;
    const caret = taRef.current ? taRef.current.selectionStart : v.length;
    const upto = v.slice(0, caret);
    const rest = v.slice(caret);
    const replaced = upto.replace(/@([\wäöüÄÖÜß.\-]*)$/, `@${u.full_name} `);
    setText(replaced + rest);
    setMentionIds((prev) => Array.from(new Set([...prev, u.id])));
    setShowMenu(false);
    setTimeout(() => taRef.current && taRef.current.focus(), 0);
  };

  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    // nur Erwähnungen behalten, deren Name noch im Text steht
    const active = mentionIds.filter((id) => {
      const u = users.find((x) => x.id === id);
      return u && body.includes(`@${u.full_name}`);
    });
    setSaving(true);
    try {
      await activityApi.addComment(entityType, entityId, body, active);
      setText(""); setMentionIds([]);
      toast.success("Kommentar gespeichert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await activityApi.deleteComment(id); toast.success("Kommentar gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Löschen"); }
  };

  const filtered = users.filter((u) => u.full_name.toLowerCase().includes(query)).slice(0, 6);

  return (
    <Card data-testid="comments-tab-content">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" />Interne Kommentare</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <textarea
            ref={taRef}
            value={text}
            onChange={onChange}
            rows={3}
            placeholder="Kommentar schreiben… mit @ Kollegen erwähnen"
            className="w-full rounded-md border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            data-testid="comment-input"
          />
          {showMenu && filtered.length > 0 && (
            <div className="absolute z-20 left-3 bottom-14 w-64 rounded-md border border-border bg-popover shadow-lg overflow-hidden" data-testid="mention-menu">
              {filtered.map((u) => (
                <button key={u.id} onClick={() => pickUser(u)} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-accent" data-testid={`mention-option-${u.id}`}>
                  <AtSign className="w-3.5 h-3.5 text-muted-foreground" />{u.full_name}
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={submit} disabled={saving || !text.trim()} data-testid="comment-submit-btn">
              <Send className="w-4 h-4 mr-2" />{saving ? "Senden…" : "Kommentieren"}
            </Button>
          </div>
        </div>

        <div className="space-y-3" data-testid="comments-list">
          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-4 text-center">Noch keine Kommentare.</p>
          ) : comments.map((c) => (
            <div key={c.id} className="flex gap-3 group" data-testid={`comment-${c.id}`}>
              <Avatar className="w-8 h-8 shrink-0"><AvatarFallback className="text-xs bg-primary/10 text-primary">{initials(c.author_name)}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{c.author_name}</span>
                  <span className="text-xs text-muted-foreground">{fmt(c.created_at)}</span>
                  {(c.author_id === user?.id || user?.role === "admin") && (
                    <button onClick={() => remove(c.id)} className="ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`comment-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap break-words mt-0.5">{renderBody(c.body)}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
